
import { defineComponent, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
    AgreementPayload,
    AgreementBillingInfoMailPayload,
    AgreementBillingInfoEmailPayload,
    AgreementBillingInfoElectronicPayload,
    AgreementType,
    GetAgreementBillingTemplate,
    useAgreementModule
} from '@/modules/agreements'
import { DeliveryPolicyType, useBillingTemplateModule } from '@/modules/billing-templates'
import AgreementBasicInfoForm from '@/components/agreements/adding-agreement/basic-info-form.vue'
import FormFieldPackages from '@/components/_shared/form-fields/packages.vue'
import AgreementBillingInfoForm from '@/components/agreements/adding-agreement/billing-info-form.vue'
import QForm from 'quasar/src/components/form/QForm.js';
import { useFormModule } from '@/modules/_shared/forms'
import { AgreementsRouteNames } from '@/router/routes-names'
import { requiredRule } from '@/modules/validations'
import AgreementFormFieldBillingTemplateId from '@/components/agreements/form-fields/billing-template-id.vue'
import { useElectronicOperatorsModule } from '@/modules/electronic-operators'
import moment from 'moment'

export default defineComponent({
    name: 'view-agreements-new-agreement-form',
    components: {
        AgreementBasicInfoForm,
        FormFieldPackages,
        AgreementBillingInfoForm,
        AgreementFormFieldBillingTemplateId
    },
    emits: ['update:modelValue'],
    async setup () {
        const router = useRouter()
        const route = useRoute()
        const { submitForm, createOnSuccess } = useFormModule()
        const {
            addAgreement,
            addAgreementWithEmailBillingInfo,
            addAgreementWithMailBillingInfo,
            addAgreementWithElectronicBillingInfo,
            createdAgreement
        } = useAgreementModule()

        const { getElectronicOperators, electronicOperators } = useElectronicOperatorsModule()
        await getElectronicOperators()

        const organizationId = route.params.organizationId as string
        const currentDate = moment().format('YYYY-MM-DD')

        const form = ref<QForm>()
        const sending = ref(false)

        const agreementPayloadModel = reactive<AgreementPayload>({
            type: AgreementType.Total,
            name: '',
            startDate: currentDate,
            endDate: null,
            invoicingAllowedFrom: currentDate,
            price: '',
            maxMembers: '',
            minLength: '',
            cancelPeriod: null,
            invoicingPeriod: '',
            billingTemplateId: '',
            packages: [],
            organizationId: organizationId,
            allowMarketingCommunication: false,
            excludeDigitalClassrooms: false,
            additionalInfo: '',
            allowLSPExport: false,
            editableInSelfService: true
        })

        const billingInfoModel = ref<GetAgreementBillingTemplate>({
            organizationId: '',
            templateName: '',
            organizationName: '',
            businessId: '',
            vatId: '',
            customerNumber: '',
            reference: '',
            contactPersonName: '',
            contactPersonEmail: '',
            street: '',
            city: '',
            zip: '',
            countryCode: '',
            billingTemplateId: '',
            paymentTerms: null,
            isDefault: false,
            deliveryPolicyType: DeliveryPolicyType.Mail,
            deliveryPolicy: {
                electronicOperatorId: '',
                electronicAddress: '',
                electronicOperatorCode: '',
                email: ''
            }
        })

        const { getBillingTemplateWithoutUpdate } = useBillingTemplateModule()
        const onBillingTemplateChange = async (value: string) => {
            if (value === null) {
                agreementPayloadModel.billingTemplateId = ''
                return
            }
            getBillingTemplateWithoutUpdate(organizationId, value).then((response) => {
                billingInfoModel.value = response
            })
        }

        const sendRequest = async () => {
            sending.value = true
            if (agreementPayloadModel.additionalInfo === '') {
                agreementPayloadModel.additionalInfo = null
            }
            const agreementPayload = {
                ...agreementPayloadModel,
                price: Math.round(+agreementPayloadModel.price * 100),
                maxMembers: +agreementPayloadModel.maxMembers,
                minLength: +agreementPayloadModel.minLength,
                cancelPeriod: agreementPayloadModel.cancelPeriod !== null ? +agreementPayloadModel.cancelPeriod : null,
                invoicingPeriod: +agreementPayloadModel.invoicingPeriod
            }

            if (agreementPayloadModel.billingTemplateId === '') {
                await addAgreement(agreementPayload)
            }

            const { organizationId, templateName, billingTemplateId, isDefault, deliveryPolicyType, deliveryPolicy, ...billingInfo } = billingInfoModel.value
            if (billingInfoModel.value.deliveryPolicyType === DeliveryPolicyType.Email) {
                const billingInfoPayload = {
                    email: billingInfoModel.value.deliveryPolicy.email,
                    agreementId: '',
                    ...billingInfo
                }
                await addAgreementWithEmailBillingInfo(agreementPayload, billingInfoPayload as AgreementBillingInfoEmailPayload)
            } else if (billingInfoModel.value.deliveryPolicyType === DeliveryPolicyType.Electronic) {
                const selectedElectronicOperator = electronicOperators.value.find(operator => +operator.id === +billingInfoModel.value.deliveryPolicy.electronicOperatorId)

                const billingInfoPayload = {
                    electronicOperatorId: billingInfoModel.value.deliveryPolicy.electronicOperatorId,
                    electronicAddress: billingInfoModel.value.deliveryPolicy.electronicAddress,
                    electronicOperatorCode: selectedElectronicOperator?.code || '',
                    ...billingInfo,
                    agreementId: ''
                }
                await addAgreementWithElectronicBillingInfo(agreementPayload, billingInfoPayload as AgreementBillingInfoElectronicPayload)
            } else {
                const billingInfoPayload = { ...billingInfo, agreementId: '' }
                await addAgreementWithMailBillingInfo(agreementPayload, billingInfoPayload as AgreementBillingInfoMailPayload)
            }
            sending.value = true
        }

        const moveToInfo = () => {
            router.push({
                name: AgreementsRouteNames.SHOW,
                params: { agreementId: createdAgreement.value.agreementId },
                query: { ...route.query }
            })
        }
        const onSubmit = () => submitForm(
            form.value,
            sendRequest,
            createOnSuccess('Agreement added'),
            null,
            () => {
                sending.value = false
            }
        )
            .then(moveToInfo)

        return {
            onSubmit,
            agreementPayloadModel,
            form,
            billingInfoModel,
            AgreementsRouteNames,
            organizationId,
            onBillingTemplateChange,
            requiredRule,
            electronicOperators,
            route,
            sending
        }
    },
    beforeCreate () {
        const { createdAgreement } = useAgreementModule()
        createdAgreement.value = { agreementId: '' }
    }
})
